/**
 * @fileoverview Sponsorships page — commercial deals and partnerships
 * Manages Title Sponsors, Kit manufacturers, sleeve sponsors, and digital partners.
 * TDS tracking per Indian IT rules.
 */

import { useState, useMemo, useEffect, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import InputLabel from "@mui/material/InputLabel";
import Alert from "@mui/material/Alert";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";

import { formatCompact, formatDateIN, CLUB_NAME } from "utils/indiaLocale";
import { PermissionGate } from "components/PermissionGate";
import { financeService } from "api";
import FootballLoader from "components/FootballLoader";
import { useAuth } from "context/AuthContext";

// ── Constants ────────────────────────────────────────────────────────────────
const SPONSORSHIP_TYPES = ["Title Sponsor", "Kit Manufacturer", "Sleeve Sponsor", "Stadium Naming Rights", "Official Partner", "Digital Partner", "Training Kit Partner", "Ground Sponsor"];
const CATEGORIES = ["Primary", "Associate", "Official Partner", "Digital", "In-Kind"];
const PAYMENT_SCHEDULES = ["Annual", "Quarterly", "Monthly", "Milestone"];
const STATUS_COLORS = { Active: "success", Negotiation: "warning", Expired: "error", Terminated: "error" };

const EMPTY_FORM = {
  sponsorName: "", sponsorshipType: "", category: "Primary",
  annualValueAmount: "", currency: "INR", tdsRate: "",
  startDate: "", endDate: "", paymentSchedule: "Annual",
  hasLogoRights: true, hasNamingRights: false, activationRights: "",
  contactName: "", contactEmail: "", notes: "",
};

function mapDto(dto) {
  return {
    id: dto.id, sponsorName: dto.sponsorName, sponsorshipType: dto.sponsorshipType,
    category: dto.category, annualValue: dto.annualValueAmount, currency: dto.currency,
    netAnnualValue: dto.netAnnualValue, tdsRate: dto.tdsRate,
    startDate: dto.startDate, endDate: dto.endDate,
    paymentSchedule: dto.paymentSchedule, status: dto.status,
    daysRemaining: dto.daysRemaining, contactName: dto.contactName,
    contactEmail: dto.contactEmail, hasLogoRights: dto.hasLogoRights,
    hasNamingRights: dto.hasNamingRights, activationRights: dto.activationRights,
    notes: dto.notes,
  };
}

// ── Component ─────────────────────────────────────────────────────────────────
function SponsorshipsPage() {
  const { currentUser } = useAuth();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("Active");
  const [categoryFilter, setCategoryFilter] = useState("All");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});

  const [closeDialog, setCloseDialog] = useState(null); // { id, closeType }

  const fetchDeals = useCallback(() => {
    setLoading(true);
    financeService.getSponsorships()
      .then((data) => setDeals(data.map(mapDto)))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchDeals(); }, [fetchDeals]);

  // ── KPIs ──────────────────────────────────────────────────────────────────
  const active = deals.filter((d) => d.status === "Active");
  const totalAnnualGross = active.reduce((s, d) => s + d.annualValue, 0);
  const totalAnnualNet = active.reduce((s, d) => s + d.netAnnualValue, 0);
  const totalTds = totalAnnualGross - totalAnnualNet;
  const expiringSoon = active.filter((d) => d.daysRemaining >= 0 && d.daysRemaining <= 90).length;

  // ── Filtered ──────────────────────────────────────────────────────────────
  const filtered = useMemo(() => deals.filter((d) => {
    const matchSearch = d.sponsorName.toLowerCase().includes(search.toLowerCase())
      || d.sponsorshipType.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === "All" || d.status === statusFilter;
    const matchCat = categoryFilter === "All" || d.category === categoryFilter;
    return matchSearch && matchStatus && matchCat;
  }), [deals, search, statusFilter, categoryFilter]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const openAdd = () => { setEditId(null); setForm(EMPTY_FORM); setFormErrors({}); setDialogOpen(true); };
  const openEdit = (d) => {
    setEditId(d.id);
    setForm({
      sponsorName: d.sponsorName, sponsorshipType: d.sponsorshipType, category: d.category,
      annualValueAmount: String(d.annualValue), currency: d.currency,
      tdsRate: d.tdsRate ? String(d.tdsRate) : "",
      startDate: d.startDate, endDate: d.endDate, paymentSchedule: d.paymentSchedule,
      hasLogoRights: d.hasLogoRights, hasNamingRights: d.hasNamingRights,
      activationRights: d.activationRights || "", contactName: d.contactName || "",
      contactEmail: d.contactEmail || "", notes: d.notes || "",
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const fc = (field, val) => setForm((prev) => ({ ...prev, [field]: val }));

  const validate = () => {
    const e = {};
    if (!form.sponsorName.trim()) e.sponsorName = "Required";
    if (!form.sponsorshipType) e.sponsorshipType = "Required";
    if (!form.annualValueAmount || isNaN(Number(form.annualValueAmount))) e.annualValueAmount = "Valid amount required";
    if (!form.startDate) e.startDate = "Required";
    if (!form.endDate) e.endDate = "Required";
    return e;
  };

  const handleSave = async () => {
    const errors = validate();
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }
    setLoading(true);
    try {
      const payload = {
        sponsorName: form.sponsorName, sponsorshipType: form.sponsorshipType,
        category: form.category, annualValueAmount: Number(form.annualValueAmount),
        currency: form.currency, tdsRate: form.tdsRate ? Number(form.tdsRate) : null,
        startDate: form.startDate, endDate: form.endDate, paymentSchedule: form.paymentSchedule,
        hasLogoRights: form.hasLogoRights, hasNamingRights: form.hasNamingRights,
        activationRights: form.activationRights || null, contactName: form.contactName || null,
        contactEmail: form.contactEmail || null, notes: form.notes || null,
        tenantId: currentUser.tenantId,
      };
      if (editId) {
        await financeService.updateSponsorship(editId, payload);
      } else {
        await financeService.createSponsorship(payload);
      }
      await fetchDeals();
      setDialogOpen(false);
    } catch (err) {
      setFormErrors({ submit: err?.title ?? err?.message ?? "Save failed" });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = async (closeType) => {
    if (!closeDialog) return;
    setLoading(true);
    try {
      await financeService.closeSponsorship(closeDialog.id, closeType);
      await fetchDeals();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      setCloseDialog(null);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        {/* Header */}
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <SoftBox>
            <SoftTypography variant="h4" fontWeight="bold" color="dark">Sponsorships</SoftTypography>
            <SoftTypography variant="body2" color="text">
              Commercial partnerships &amp; revenue · TDS tracking · {CLUB_NAME}
            </SoftTypography>
          </SoftBox>
          <PermissionGate permission="finance.edit">
            <SoftButton variant="gradient" color="info" onClick={openAdd}>
              <Icon sx={{ mr: 0.5 }}>add</Icon>New Deal
            </SoftButton>
          </PermissionGate>
        </SoftBox>

        {expiringSoon > 0 && (
          <SoftBox mb={3}>
            <Alert severity="warning">
              <strong>{expiringSoon} sponsorship deal{expiringSoon > 1 ? "s" : ""}</strong> expiring within 90 days — renew or renegotiate before expiry.
            </Alert>
          </SoftBox>
        )}

        {/* KPIs */}
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard title={{ text: "Active Deals" }} count={active.length}
                percentage={{ color: "success", text: "commercial partners" }}
                icon={{ color: "success", component: "handshake" }} />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard title={{ text: "Annual Gross" }} count={formatCompact(totalAnnualGross)}
                percentage={{ color: "info", text: "total contracted value" }}
                icon={{ color: "info", component: "monetization_on" }} />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard title={{ text: "Net After TDS" }} count={formatCompact(totalAnnualNet)}
                percentage={{ color: "success", text: `TDS: ${formatCompact(totalTds)}` }}
                icon={{ color: "primary", component: "account_balance_wallet" }} />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard title={{ text: "Expiring ≤90 Days" }} count={expiringSoon}
                percentage={{ color: expiringSoon > 0 ? "error" : "success", text: expiringSoon > 0 ? "renew urgently" : "all clear" }}
                icon={{ color: expiringSoon > 0 ? "error" : "success", component: "event_busy" }} />
            </Grid>
          </Grid>
        </SoftBox>

        {/* Table */}
        <Card>
          <SoftBox p={3}>
            <Grid container spacing={2} mb={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField fullWidth placeholder="Search sponsor or type…" value={search}
                  onChange={(e) => setSearch(e.target.value)} size="small"
                  InputProps={{ startAdornment: <Icon sx={{ mr: 1, color: "#aaa" }}>search</Icon> }} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select value={categoryFilter} label="Category" onChange={(e) => setCategoryFilter(e.target.value)}>
                    <MenuItem value="All">All Categories</MenuItem>
                    {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Negotiation">Negotiation</MenuItem>
                    <MenuItem value="Expired">Expired</MenuItem>
                    <MenuItem value="Terminated">Terminated</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <SoftTypography variant="caption" color="text">{filtered.length} of {deals.length}</SoftTypography>
              </Grid>
            </Grid>

            <SoftBox sx={{ overflowX: "auto" }}>
              <SoftBox component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 1000 }}>
                <SoftBox component="thead" sx={{ backgroundColor: "#f8f9fa" }}>
                  <SoftBox component="tr">
                    {["Sponsor", "Type / Category", "Annual Value", "Net (after TDS)", "Period", "Payment", "Rights", "Status", "Actions"].map((h) => (
                      <SoftBox key={h} component="th" sx={{
                        textAlign: "left", p: 1.5, color: "text.secondary",
                        fontWeight: 700, fontSize: "0.7rem", textTransform: "uppercase",
                        borderBottom: "2px solid #e9ecef", whiteSpace: "nowrap",
                      }}>{h}</SoftBox>
                    ))}
                  </SoftBox>
                </SoftBox>
                <SoftBox component="tbody">
                  {loading ? (
                    <SoftBox component="tr">
                      <SoftBox component="td" colSpan={9}><FootballLoader label="Loading sponsorships…" /></SoftBox>
                    </SoftBox>
                  ) : filtered.length === 0 ? (
                    <SoftBox component="tr">
                      <SoftBox component="td" colSpan={9} sx={{ textAlign: "center", p: 4 }}>
                        <SoftTypography variant="caption" color="text">No deals match the current filters.</SoftTypography>
                      </SoftBox>
                    </SoftBox>
                  ) : filtered.map((d, i) => (
                    <SoftBox key={d.id} component="tr" sx={{
                      "&:hover": { backgroundColor: "#f8f9fa" },
                      backgroundColor: i % 2 === 0 ? "white" : "#fdfdfd",
                    }}>
                      <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                        <SoftTypography variant="button" fontWeight="medium">{d.sponsorName}</SoftTypography>
                        {d.contactName && (
                          <SoftTypography variant="caption" color="text" display="block">{d.contactName}</SoftTypography>
                        )}
                      </SoftBox>
                      <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                        <SoftTypography variant="caption" color="text">{d.sponsorshipType}</SoftTypography>
                        <br />
                        <SoftBadge badgeContent={d.category} color="info" size="xs" container />
                      </SoftBox>
                      <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                        <SoftTypography variant="button" fontWeight="medium">{formatCompact(d.annualValue)}</SoftTypography>
                        {d.tdsRate && (
                          <SoftTypography variant="caption" color="warning" display="block">{d.tdsRate}% TDS</SoftTypography>
                        )}
                      </SoftBox>
                      <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                        <SoftTypography variant="button" fontWeight="bold" color="dark">
                          {formatCompact(d.netAnnualValue)}
                        </SoftTypography>
                      </SoftBox>
                      <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0", whiteSpace: "nowrap" }}>
                        <SoftTypography variant="caption" color="text">
                          {formatDateIN(d.startDate)} → {formatDateIN(d.endDate)}
                        </SoftTypography>
                        {d.daysRemaining >= 0 && d.daysRemaining <= 90 && (
                          <Chip label={`${d.daysRemaining}d left`} size="small" color="warning" sx={{ ml: 1, fontSize: "0.65rem", height: 18 }} />
                        )}
                      </SoftBox>
                      <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                        <SoftTypography variant="caption" color="text">{d.paymentSchedule}</SoftTypography>
                      </SoftBox>
                      <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                        <SoftBox display="flex" gap={0.5} flexWrap="wrap">
                          {d.hasLogoRights && <Chip label="Logo" size="small" sx={{ fontSize: "0.6rem", height: 16 }} />}
                          {d.hasNamingRights && <Chip label="Naming" size="small" color="primary" sx={{ fontSize: "0.6rem", height: 16 }} />}
                        </SoftBox>
                      </SoftBox>
                      <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                        <SoftBadge badgeContent={d.status} color={STATUS_COLORS[d.status] || "info"} size="xs" container />
                      </SoftBox>
                      <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                        <PermissionGate permission="finance.edit">
                          <SoftBox display="flex" gap={0.5}>
                            <Tooltip title="Edit">
                              <SoftButton variant="text" color="info" size="small" onClick={() => openEdit(d)} sx={{ minWidth: 0, p: 0.5 }}>
                                <Icon>edit</Icon>
                              </SoftButton>
                            </Tooltip>
                            {d.status === "Active" && (
                              <Tooltip title="Close Deal">
                                <SoftButton variant="text" color="warning" size="small"
                                  onClick={() => setCloseDialog({ id: d.id })} sx={{ minWidth: 0, p: 0.5 }}>
                                  <Icon>close</Icon>
                                </SoftButton>
                              </Tooltip>
                            )}
                          </SoftBox>
                        </PermissionGate>
                      </SoftBox>
                    </SoftBox>
                  ))}
                </SoftBox>
              </SoftBox>
            </SoftBox>
          </SoftBox>
        </Card>
      </SoftBox>
      <Footer company={{ href: "#", name: CLUB_NAME }} links={[]} />

      {/* ── Add / Edit Dialog ────────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem" }}>
          {editId ? "Edit Sponsorship Deal" : "New Sponsorship Deal"}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} pt={1}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Sponsor Name" required value={form.sponsorName}
                onChange={(e) => fc("sponsorName", e.target.value)}
                error={!!formErrors.sponsorName} helperText={formErrors.sponsorName} size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" error={!!formErrors.sponsorshipType}>
                <InputLabel shrink>Sponsorship Type</InputLabel>
                <Select value={form.sponsorshipType} label="Sponsorship Type" onChange={(e) => fc("sponsorshipType", e.target.value)}>
                  {SPONSORSHIP_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Category</InputLabel>
                <Select value={form.category} label="Category" onChange={(e) => fc("category", e.target.value)}>
                  {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Payment Schedule</InputLabel>
                <Select value={form.paymentSchedule} label="Payment Schedule" onChange={(e) => fc("paymentSchedule", e.target.value)}>
                  {PAYMENT_SCHEDULES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Currency</InputLabel>
                <Select value={form.currency} label="Currency" onChange={(e) => fc("currency", e.target.value)}>
                  {["INR", "USD", "EUR", "GBP"].map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth label="Annual Value" required type="number" value={form.annualValueAmount}
                onChange={(e) => fc("annualValueAmount", e.target.value)}
                error={!!formErrors.annualValueAmount} helperText={formErrors.annualValueAmount} size="small"
                inputProps={{ min: 0 }} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth label="TDS Rate %" type="number" value={form.tdsRate}
                onChange={(e) => fc("tdsRate", e.target.value)} size="small"
                inputProps={{ min: 0, max: 100 }} InputLabelProps={{ shrink: true }}
                helperText="Optional — auto-deducted from gross" />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth label="Net Value (preview)" value={
                form.annualValueAmount
                  ? formatCompact(Number(form.annualValueAmount) * (1 - (Number(form.tdsRate) || 0) / 100))
                  : "—"
              } size="small" InputProps={{ readOnly: true }}
                sx={{ "& .MuiInputBase-root": { backgroundColor: "#f8f9fa" } }} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Start Date" required type="date" value={form.startDate}
                onChange={(e) => fc("startDate", e.target.value)}
                error={!!formErrors.startDate} helperText={formErrors.startDate} size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="End Date" required type="date" value={form.endDate}
                onChange={(e) => fc("endDate", e.target.value)}
                error={!!formErrors.endDate} helperText={formErrors.endDate} size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Contact Name" value={form.contactName}
                onChange={(e) => fc("contactName", e.target.value)} size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Contact Email" type="email" value={form.contactEmail}
                onChange={(e) => fc("contactEmail", e.target.value)} size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Activation Rights" value={form.activationRights}
                onChange={(e) => fc("activationRights", e.target.value)} size="small"
                placeholder="e.g. Jersey Front, Stadium Boards, Digital Banners"
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={<Checkbox checked={form.hasLogoRights} onChange={(e) => fc("hasLogoRights", e.target.checked)} />}
                label={<SoftTypography variant="caption">Logo Rights</SoftTypography>}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={<Checkbox checked={form.hasNamingRights} onChange={(e) => fc("hasNamingRights", e.target.checked)} />}
                label={<SoftTypography variant="caption">Naming Rights</SoftTypography>}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Notes" multiline rows={2} value={form.notes}
                onChange={(e) => fc("notes", e.target.value)} size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
            {formErrors.submit && (
              <Grid item xs={12}><Alert severity="error">{formErrors.submit}</Alert></Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <SoftButton variant="outlined" color="secondary" onClick={() => setDialogOpen(false)}>Cancel</SoftButton>
          <SoftButton variant="gradient" color="info" onClick={handleSave}>
            {editId ? "Update" : "Create"} Deal
          </SoftButton>
        </DialogActions>
      </Dialog>

      {/* ── Close Deal Dialog ────────────────────────────────────────────── */}
      <Dialog open={!!closeDialog} onClose={() => setCloseDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem" }}>Close Sponsorship Deal</DialogTitle>
        <DialogContent>
          <SoftTypography variant="body2" color="text" mb={2}>
            How should this deal be closed?
          </SoftTypography>
          <SoftBox display="flex" gap={1} justifyContent="center">
            <SoftButton variant="outlined" color="warning" onClick={() => handleClose("Expired")}>
              <Icon sx={{ mr: 0.5 }}>event_busy</Icon>Mark Expired
            </SoftButton>
            <SoftButton variant="outlined" color="error" onClick={() => handleClose("Terminated")}>
              <Icon sx={{ mr: 0.5 }}>cancel</Icon>Terminate
            </SoftButton>
          </SoftBox>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <SoftButton variant="outlined" color="secondary" onClick={() => setCloseDialog(null)}>Cancel</SoftButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default SponsorshipsPage;
